import mustache from 'mustache';
import { maplayerSelector, mapLoadingIcon, mapLoadingIcon_genieJourneySearch } from '../../util/mustache_templates';
import { featureFlagKeys, getFeatureFlags } from '../../util/general_util';

import { Feature, View } from "ol";
import Map from "ol/Map";
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import Control from 'ol/control/Control';
import Zoom from 'ol/control/Zoom';
import ScaleLine from 'ol/control/ScaleLine';
import { Vector as Vector_src, XYZ } from "ol/source";
import { Style as Vector_style } from 'ol/style';
import { Vector } from 'ol/layer';
import { highlightedMarkerStyle, mapLayerIds, potentialStayStyle, routeStyle, stayMarkerStyle, stopCircleStyle_agStops, stopCircleStyle_userStop, stopMarkerStyle } from './static_defs';
import Popup from 'ol/Overlay';
import { useGeographic } from 'ol/proj';
import { init_hereMaps_layers } from './helper_functions';
import { getOpenRoads_onRoute, getPoiOnRouteControl, getPOIs_onRoute } from '../api_utils';
import { LineString } from 'ol/geom';
import { add_pois_on_route, center_map, remove_openRoads_on_route, remove_pois_on_route } from './map_displays';

import MouseWheelZoom from 'ol/interaction/MouseWheelZoom';
import { log_openRoadsClick } from '../../log';

enum OverlayPositioning {
    BOTTOM_LEFT = 'bottom-left',
    BOTTOM_CENTER = 'bottom-center',
    BOTTOM_RIGHT = 'bottom-right',
    CENTER_LEFT = 'center-left',
    CENTER_CENTER = 'center-center',
    CENTER_RIGHT = 'center-right',
    TOP_LEFT = 'top-left',
    TOP_CENTER = 'top-center',
    TOP_RIGHT = 'top-right',
}



function createMap_OSMTiles() {
	let map:Map = new Map({
		view: new View({
		  center: [-95, 37],
		  zoom: 5,
		  maxZoom: 15
		}),
		layers: [
		  new TileLayer({
			source: new OSM(),
			zIndex: 0
		  })
		],
		target: 'tripPlannerMap',
		controls: [
			new Zoom(),
			new ScaleLine({units:"imperial"})
		]
	});
	return map;
};

function createMap_hereMapTiles() {
	const mapLayers:TileLayer<XYZ>[] = init_hereMaps_layers();

	let map:Map = new Map({
		view: new View({
		  center: [-95, 37],
		  zoom: 5,
		  maxZoom: 15
		}),
		layers: mapLayers,
		target: 'tripPlannerMap',
		controls: [
			new Zoom({
				className: "position-absolute end-0 me-2 mt-3",
                zoomInClassName: "",
                zoomOutClassName: ""
			}),
			// new ScaleLine({units:"imperial"}),
			new Control({
				element: $(mustache.render(maplayerSelector, {}))[0]
			})
		]
	});
	return map;
};

function addLayer(map:Map, style:Vector_style|null, zIndex:number) {
    const props = {
        source: new Vector_src(),
        style: style,
        zIndex: zIndex
    };
    if (style != null) props.style
    const layer = new Vector(props);
    map.addLayer(layer);
    return layer;
};

function addPopup(map:Map, id:string, positioning:OverlayPositioning, offset:number[], element:HTMLElement|null) {
    const popup = new Popup({
        id: id,
        positioning: positioning,
        offset: offset
    });
    if (element != null) {
        popup.setElement(element);
    };
    map.addOverlay(popup);
    return popup;
};

export async function initMap_OPENLAYERS() {
    useGeographic();

	let map:Map;
	if (window.featureFlags == undefined) {
		let featureFlags = await getFeatureFlags(featureFlagKeys);
		window.featureFlags = featureFlags;
	};

	if (window.featureFlags.mapTileProvider == "hereMaps") {
		map = createMap_hereMapTiles();
	} else {
		map = createMap_OSMTiles();
	};

    // Modify the zoom sensitivity by adjusting mouseWheelZoom interaction
    map.getInteractions().forEach(function(interaction) {
        if (interaction instanceof MouseWheelZoom) {
        interaction.setActive(false); // Disable existing MouseWheelZoom
        }
    });

    // Create and configure MouseWheelZoom interaction manually with better sensitivity control
    const mouseWheelZoom = new MouseWheelZoom({
        duration: 500, // Smoothen the zoom duration
        constrainResolution: true, // Snap to zoom levels
    });

    map.addInteraction(mouseWheelZoom);


	window.mainTripPlannerMap = map;

    window.routeLayer = addLayer(map, routeStyle, 1);
    window.stayMarkerLayer = addLayer(map, stayMarkerStyle, 7);
    window.mainMapMarkerLayer = addLayer(map, stopMarkerStyle, 5);
    window.clusterMarkerLayer = addLayer(map, null, 5);
    window.highlightedMarkerLayer = addLayer(map, highlightedMarkerStyle, 5);
    window.zoomedClusterMarkerLayer_userStops = addLayer(map, stopCircleStyle_userStop, 5);
    window.zoomedClusterMarkerLayer_agStops = addLayer(map, stopCircleStyle_agStops, 5);

    window.poiMarkerLayer = addLayer(map, new Vector_style(), 6);
    window.stopPoisMarkerLayer = addLayer(map, new Vector_style(), 6);

    window.rvShareMarkerLayer = addLayer(map, new Vector_style(), 6);

    window.poiOnRouteMarkerLayer = addLayer(map, new Vector_style(), 6);

    window.map_campInfoPopup = addPopup(
        map,
        "markerClickPopup",
        OverlayPositioning.BOTTOM_RIGHT,
        [0,-20],
        null
    );

    const map_stay_mobile:HTMLElement = document.createElement("div");
    map_stay_mobile.id = "map-stay-mobile";
    map_stay_mobile.className = "d-none position-absolute bottom-0 start-50";
    map_stay_mobile.style.transform = "translateX(-58%)";
    map_stay_mobile.style.zIndex = "100";
    map_stay_mobile.style.width = "calc(100% - 3rem)";
    map_stay_mobile.style.marginLeft = "1.5rem";
    map_stay_mobile.style.marginRight = "1.5rem";
    window.map_campInfoPopup_mobile = new Control({
        element: map_stay_mobile,
    });
    map.addControl(window.map_campInfoPopup_mobile);

    window.map_loadingIcon_genieJourney = addPopup(
        map,
        "mapLoadingIcon-genieJourneySearch",
        OverlayPositioning.CENTER_CENTER,
        [0,0],
        $(mustache.render(mapLoadingIcon_genieJourneySearch,{}))[0]
    );
    window.map_poiInfoPopup = addPopup(
        map,
        "poiClickPopup",
        OverlayPositioning.BOTTOM_LEFT,
        [0,-20],
        null
    );
    window.map_routeWarningPopup = addPopup(
        map,
        "routeWarningPopup",
        OverlayPositioning.BOTTOM_LEFT,
        [0,-20],
        null
    );
    window.map_rvRentalPopup = addPopup(
        map,
        "rvRentalPopup",
        OverlayPositioning.BOTTOM_CENTER,
        [0,-20],
        null
    );

    getPoiOnRouteControl().then(controlHtml => {
        const control = new Control({
            element: $(controlHtml)[0]
        });
        map.addControl(control);

        $("#otherFuelLink").on("click", function() {
            remove_openRoads_on_route();
            $("[open-roads-on-route]").each(function() {
                $(this).prop("checked",false);
            });
        });

        $("#openRoadsLink").on("click", function() {
            $("#onRoute-otherFuel input").each(function() {
                $(this).prop("checked",false);
                remove_pois_on_route(parseInt(this.getAttribute("poi-type")));
            });

            $("#OpenRoads-all").prop("checked",true)
            const mapBounds = map.getView().calculateExtent(map.getSize());
            const dist:number = 5000 * map.getView().getZoom();
            const geom:LineString = new LineString(window.tripPlannerCurrentTrip.directions.routes[0].geometry.coordinates).simplify(0.5) as LineString;
            getOpenRoads_onRoute(geom.getCoordinates(), mapBounds, dist).then(pois => {
                pois.forEach(poi => {
                    $("[open-roads-on-route][poi-type='"+ poi['poi_typeID']+"']").prop("checked",true);
                });

                if (!window.poisOnRouteActive) window.poisOnRouteActive = true;
                add_pois_on_route(pois,true);
                
            }).catch(e => {
                $(this).prop("checked",false);
                if (e.message == "invalid-access") {
                    if (Number.isNaN(parseInt(window.GAsubLevel))) {
                        //not signed in
                        $("#signUp_cardedTrialModal").find("[signUpMsg]").html("Sign up to view POIs along your route");
                        $("#signUp_cardedTrialModal").modal("show");
                    } else {
                        $("#freemium-carded-trial").find("[signUpMsg]").html("Start your free trial to view POIs along your route");
                        $("#freemium-carded-trial").modal("show");
                    };
                } else {
                    console.error(e);
                }
            });
        });

        $("[poi-on-route]").on("change", function() {  
            const typeID:number = parseInt(this.getAttribute("poi-type"));
            if (Number.isNaN(typeID)) return;
            if ($(this).prop("checked")) {
                if ($("#stopPopout").is(":visible")) {
                    $("#stopPopout").toggleClass("d-none d-flex");
                    document.getElementById("stopPopout").dispatchEvent(new CustomEvent("element.hide", {
                        bubbles: false,
                        cancelable: true
                    }));
                };

                const mapBounds = map.getView().calculateExtent(map.getSize());
                const dist:number = 5000 * map.getView().getZoom();
                const geom:LineString = new LineString(window.tripPlannerCurrentTrip.directions.routes[0].geometry.coordinates).simplify(0.5) as LineString;
                getPOIs_onRoute(geom.getCoordinates(), mapBounds, dist, [typeID],$("#OpenRoads").prop("checked")).then(pois => {
                    if ($(this).prop("checked")) {
                        if (!window.poisOnRouteActive) window.poisOnRouteActive = true;
                        add_pois_on_route(pois,$("#OpenRoads").prop("checked"));
                    };
                }).catch(e => {
                    $(this).prop("checked",false);
                    if (e.message == "invalid-access") {
                        if (Number.isNaN(parseInt(window.GAsubLevel))) {
                            //not signed in
                            $("#signUp_cardedTrialModal").find("[signUpMsg]").html("Sign up to view POIs along your route");
                            $("#signUp_cardedTrialModal").modal("show");
                        } else {
                            $("#freemium-carded-trial").find("[signUpMsg]").html("Start your free trial to view POIs along your route");
                            $("#freemium-carded-trial").modal("show");
                        };
                    } else {
                        console.error(e);
                    };
                });
            } else {
                remove_pois_on_route(typeID);

                if ($("[poi-on-route]:checked").length == 0) {
                    window.poisOnRouteActive = false
                };
            };
        });
        $("[open-roads-on-route]").on("change",function() {
            //get check fuel poi route types
            const mapBounds = map.getView().calculateExtent(map.getSize());
            const dist:number = 5000 * map.getView().getZoom();
            const geom:LineString = new LineString(window.tripPlannerCurrentTrip.directions.routes[0].geometry.coordinates).simplify(0.5) as LineString;
            if ($(this).prop("checked")) {
                if ($(this).attr("id") === "OpenRoads-all") {
                    getOpenRoads_onRoute(geom.getCoordinates(), mapBounds, dist).then(pois => {
                        pois.forEach(poi => {
                            $("[open-roads-on-route][poi-type='"+ poi['poi_typeID']+"']").prop("checked",true);
                        });

                        if (!window.poisOnRouteActive) window.poisOnRouteActive = true;
                        add_pois_on_route(pois,true);
                        
                    }).catch(e => {
                        $(this).prop("checked",false);
                        if (e.message == "invalid-access") {
                            if (Number.isNaN(parseInt(window.GAsubLevel))) {
                                //not signed in
                                $("#signUp_cardedTrialModal").find("[signUpMsg]").html("Sign up to view POIs along your route");
                                $("#signUp_cardedTrialModal").modal("show");
                            } else {
                                $("#freemium-carded-trial").find("[signUpMsg]").html("Start your free trial to view POIs along your route");
                                $("#freemium-carded-trial").modal("show");
                            };
                        } else {
                            console.error(e);
                        }
                    });
                } else {
                    getPOIs_onRoute(geom.getCoordinates(),mapBounds,dist,[parseInt($(this).attr("poi-type"))],true).then(pois => {
                        if (!window.poisOnRouteActive) window.poisOnRouteActive = true;
                        add_pois_on_route(pois,true);
                    }).catch(e => {
                        $(this).prop("checked",false);
                        if (e.message == "invalid-access") {
                            if (Number.isNaN(parseInt(window.GAsubLevel))) {
                                //not signed in
                                $("#signUp_cardedTrialModal").find("[signUpMsg]").html("Sign up to view POIs along your route");
                                $("#signUp_cardedTrialModal").modal("show");
                            } else {
                                $("#freemium-carded-trial").find("[signUpMsg]").html("Start your free trial to view POIs along your route");
                                $("#freemium-carded-trial").modal("show");
                            };
                        } else {
                            console.error(e);
                        }
                    });
                }
            } else {                
                remove_pois_on_route(parseInt($(this).attr("poi-type")));
                if ($("[open-on-route]:checked").length == 0) {
                    window.poisOnRouteActive = false
                    return;
                };
            }
        });
    });
    return map;
};
